import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  GraduationCap, 
  User, 
  BookOpen, 
  Upload,
  Loader2,
  ArrowLeft,
  ArrowRight,
  Save,
  CheckCircle2,
  LogOut
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Document types for upload
const documentTypes = [
  { id: 'photo', name: 'Passport Size Photo', required: true },
  { id: 'aadhar', name: 'Aadhar Card', required: false },
  { id: 'class_10_marksheet', name: '10th Marksheet', required: false },
  { id: 'class_12_marksheet', name: '12th Marksheet', required: false },
  { id: 'transfer_certificate', name: 'Transfer Certificate', required: false },
  { id: 'other', name: 'Other Document', required: false }
];

// District data by state
const districtsByState = {
  "West Bengal": ["Kolkata", "Howrah", "North 24 Parganas", "South 24 Parganas", "Hooghly", "Nadia", "Murshidabad", "Bardhaman", "Purba Bardhaman", "Paschim Bardhaman", "Birbhum", "Bankura", "Purulia", "Medinipur", "Paschim Medinipur", "Purba Medinipur", "Jhargram", "Malda", "Uttar Dinajpur", "Dakshin Dinajpur", "Darjeeling", "Jalpaiguri", "Cooch Behar", "Alipurduar"],
  "Bihar": ["Patna", "Gaya", "Bhagalpur", "Muzaffarpur", "Darbhanga", "Purnia", "Munger", "Nalanda", "Vaishali", "Begusarai"],
  "Jharkhand": ["Ranchi", "Jamshedpur", "Dhanbad", "Bokaro", "Hazaribagh", "Deoghar", "Giridih", "Dumka"],
  "Odisha": ["Bhubaneswar", "Cuttack", "Puri", "Rourkela", "Berhampur", "Sambalpur", "Balasore"],
  "Assam": ["Guwahati", "Dibrugarh", "Jorhat", "Silchar", "Nagaon", "Tezpur", "Tinsukia"],
  "Delhi": ["Central Delhi", "East Delhi", "New Delhi", "North Delhi", "North East Delhi", "North West Delhi", "Shahdara", "South Delhi", "South East Delhi", "South West Delhi", "West Delhi"],
  "Uttar Pradesh": ["Lucknow", "Kanpur", "Agra", "Varanasi", "Prayagraj", "Ghaziabad", "Noida", "Meerut", "Aligarh", "Bareilly"],
  "Maharashtra": ["Mumbai", "Pune", "Nagpur", "Thane", "Nashik", "Aurangabad", "Solapur", "Kolhapur"],
  "Karnataka": ["Bengaluru", "Mysuru", "Mangaluru", "Hubli", "Belgaum", "Gulbarga", "Davangere"],
  "Tamil Nadu": ["Chennai", "Coimbatore", "Madurai", "Salem", "Tiruchirappalli", "Tirunelveli", "Vellore"],
  "Other": ["Other"]
};

// Occupation options
const occupationOptions = [
  "Government Service",
  "Private Service",
  "Business",
  "Self-Employed",
  "Farmer/Agriculture",
  "Teacher/Professor",
  "Doctor/Medical",
  "Engineer",
  "Lawyer/Advocate",
  "Bank Employee",
  "Police/Defence",
  "Daily Wage Worker",
  "Homemaker",
  "Retired",
  "Not Employed",
  "Other"
];

export default function StudentFormFill() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [lockedFields, setLockedFields] = useState({});
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [documentSettings, setDocumentSettings] = useState([]);
  const [studentCourseId, setStudentCourseId] = useState(null);
  const [formData, setFormData] = useState({
    // Personal Details
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    phone: '',
    whatsapp_no: '',
    date_of_birth: '',
    gender: 'Male',
    religion: '',
    caste_category: '',
    blood_group: '',
    aadhar_no: '',
    nationality: 'Indian',
    medium_of_instruction: '',
    // Address
    address: '',
    city: '',
    state: '',
    district: '',
    pin_code: '',
    // Academic - 10th
    class_10_board: '',
    class_10_school: '',
    class_10_year: '',
    class_10_percentage: '',
    // Academic - 12th
    class_12_board: '',
    class_12_school: '',
    class_12_year: '',
    class_12_percentage: '',
    // Parents
    father_name: '',
    father_phone: '',
    father_occupation: '',
    mother_name: '',
    mother_phone: '',
    mother_occupation: '',
  });

  const [languages, setLanguages] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchFormData();
    fetchLanguages();
    fetchStudentCourseAndDocuments();
  }, [navigate]);

  // Fetch student's course and then document settings for that course
  const fetchStudentCourseAndDocuments = async () => {
    const token = localStorage.getItem('studentToken');
    try {
      // First get student profile to find course_id
      const profileResponse = await fetch(`${API_URL}/api/student-portal/profile`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        const courseId = profileData.course_id;
        setStudentCourseId(courseId);
        
        if (courseId) {
          // Fetch document settings for this course (include_defaults=true for default documents)
          const docResponse = await fetch(`${API_URL}/api/document-settings/?course_id=${courseId}&include_defaults=true`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          
          if (docResponse.ok) {
            const docSettings = await docResponse.json();
            setDocumentSettings(docSettings);
            console.log('📄 Document settings loaded:', docSettings.length, 'documents');
          }
        }
      }
    } catch (error) {
      console.error('Error fetching document settings:', error);
      // Fallback to default documentTypes
      setDocumentSettings([]);
    }
  };

  // Get effective document types (from settings or fallback to default)
  const getEffectiveDocumentTypes = () => {
    if (documentSettings.length > 0) {
      return documentSettings.map(doc => ({
        id: doc.document_name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
        name: doc.document_name,
        required: doc.is_mandatory,
        allowed_formats: doc.allowed_formats,
        max_size_kb: doc.max_size_kb,
        description: doc.description
      }));
    }
    // Fallback to default documentTypes if no settings
    return documentTypes;
  };

  const fetchLanguages = async () => {
    const token = localStorage.getItem('studentToken');
    try {
      const response = await fetch(`${API_URL}/api/master-settings/languages`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLanguages(data);
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
    }
  };

  const fetchFormData = async () => {
    const token = localStorage.getItem('studentToken');
    
    try {
      const response = await fetch(`${API_URL}/api/student-portal/form-data`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('studentToken');
          navigate('/student/login');
          return;
        }
        if (response.status === 400) {
          toast.info('Your form has already been submitted');
          navigate('/student/dashboard');
          return;
        }
        throw new Error('Failed to fetch form data');
      }
      
      const data = await response.json();
      setLockedFields(data.locked_fields || {});
      
      if (data.form_data) {
        setFormData(prev => ({
          ...prev,
          ...data.form_data,
        }));
        
        // Load previously uploaded documents
        if (data.form_data.documents && Array.isArray(data.form_data.documents)) {
          setUploadedDocuments(data.form_data.documents);
          console.log('📄 Loaded', data.form_data.documents.length, 'documents from server');
          
          // Set photo_url if photo document exists
          const photoDoc = data.form_data.documents.find(d => 
            d.type === 'photo' || d.type === 'passport_photo' || d.type === 'passport_size_photo'
          );
          if (photoDoc?.data) {
            setFormData(prev => ({ ...prev, photo_url: photoDoc.data }));
          }
        }
      }
    } catch (error) {
      console.error('Form data fetch error:', error);
      toast.error('Failed to load form data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Clear district when state changes
      ...(name === 'state' && { district: '' })
    }));
  };

  // Get districts based on selected state
  const getDistricts = () => {
    if (!formData.state) return [];
    return districtsByState[formData.state] || districtsByState["Other"] || [];
  };

  // Handle document file upload
  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Get document settings for validation
    const docSettings = getEffectiveDocumentTypes().find(d => d.id === docType);
    const maxSizeBytes = (docSettings?.max_size_kb || 5000) * 1024; // Convert KB to bytes
    
    // Validate file size
    if (file.size > maxSizeBytes) {
      toast.error(`File size should be less than ${docSettings?.max_size_kb || 5000}KB`);
      return;
    }
    
    // Validate file type
    let allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (docSettings?.allowed_formats) {
      allowedTypes = docSettings.allowed_formats.map(f => {
        if (f === 'pdf') return 'application/pdf';
        if (f === 'jpg' || f === 'jpeg') return 'image/jpeg';
        return `image/${f}`;
      });
    }
    if (!allowedTypes.includes(file.type)) {
      toast.error(`Only ${docSettings?.allowed_formats?.join(', ') || 'JPG, PNG or PDF'} files are allowed`);
      return;
    }
    
    setUploadingDoc(docType);
    
    try {
      // Smart reader: compresses images, passes PDFs through
      const { readFileSmart } = await import('@/utils/imageCompressor');
      const result = await readFileSmart(file);
      const base64 = result.data;
      // Get name from effective document types (dynamic or fallback)
      const docName = getEffectiveDocumentTypes().find(d => d.id === docType)?.name || docType;
      const newDoc = {
        type: docType,
        name: docName,
        file_name: file.name,
        file_type: file.type,
        file_size: result.size,
        data: base64,
        uploaded_at: new Date().toISOString()
      };
      
      // Upload to server immediately (one document at a time)
      try {
        const token = localStorage.getItem('studentToken');
        const response = await fetch(`${API_URL}/api/student-portal/upload-single-document`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newDoc),
        });
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to upload document');
        }
        
        const result = await response.json();
        console.log('📄 Document saved to server:', result);
        
        // Update local state
        setUploadedDocuments(prev => {
          const filtered = prev.filter(d => d.type !== docType);
          return [...filtered, newDoc];
        });
        
        // If it's a photo, save the URL for preview
        if (docType === 'photo' || docType === 'passport_photo') {
          setFormData(prev => ({ ...prev, photo_url: base64 }));
        }
        
        toast.success(`${newDoc.name} uploaded and saved! (${result.total_documents} documents total)`);
      } catch (uploadError) {
        console.error('Server upload error:', uploadError);
        toast.error(uploadError.message || 'Failed to save document to server');
      }
      
      setUploadingDoc(null);
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
      setUploadingDoc(null);
    }
  };

  // Remove uploaded document
  const removeDocument = (docType) => {
    setUploadedDocuments(prev => prev.filter(d => d.type !== docType));
    if (docType === 'photo' || docType === 'passport_photo') {
      setFormData(prev => ({ ...prev, photo_url: null }));
    }
    toast.info('Document removed');
  };

  // Check if document is uploaded
  const isDocumentUploaded = (docType) => {
    return uploadedDocuments.some(d => d.type === docType);
  };

  // Get uploaded document preview URL
  const getDocumentPreview = (docType) => {
    const doc = uploadedDocuments.find(d => d.type === docType);
    return doc?.data || null;
  };

  const handleSubmit = async () => {
    const token = localStorage.getItem('studentToken');
    
    // Basic validation
    if (!formData.first_name || !formData.last_name || !formData.date_of_birth) {
      toast.error('Name and Date of Birth are required');
      return;
    }
    
    setIsSaving(true);
    
    try {
      // Include documents in the submission
      const submitData = {
        ...formData,
        documents: uploadedDocuments
      };
      
      const response = await fetch(`${API_URL}/api/student-portal/form-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit form');
      }
      
      toast.success('Form submitted successfully!');
      navigate('/student/dashboard');
    } catch (error) {
      console.error('Form submit error:', error);
      toast.error(error.message || 'Failed to submit form');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('studentToken');
    localStorage.removeItem('studentInfo');
    navigate('/student/login');
  };

  const steps = [
    { id: 1, title: 'Personal Details', icon: User },
    { id: 2, title: 'Academic Details', icon: BookOpen },
    { id: 3, title: 'Family Details', icon: User },
    { id: 4, title: 'Documents', icon: Upload },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Admission Form</h1>
                <p className="text-sm text-gray-500">
                  {lockedFields.course_name} • {lockedFields.session_name}
                </p>
              </div>
            </div>
            <Button variant="ghost" onClick={handleLogout} className="text-red-600 hover:text-red-700">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div 
                  className={`flex items-center gap-2 ${
                    currentStep >= step.id ? 'text-indigo-600' : 'text-gray-400'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    currentStep >= step.id ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {currentStep > step.id ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <step.icon className="w-4 h-4" />
                    )}
                  </div>
                  <span className="hidden sm:block text-sm font-medium">{step.title}</span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 ${
                    currentStep > step.id ? 'bg-indigo-600' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <Card>
          <CardHeader>
            <CardTitle>{steps[currentStep - 1].title}</CardTitle>
            <CardDescription>
              Please fill all details correctly. Fields marked with * are required.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Step 1: Personal Details */}
            {currentStep === 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    placeholder="First name"
                    required
                    data-testid="first-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="middle_name">Middle Name</Label>
                  <Input
                    id="middle_name"
                    name="middle_name"
                    value={formData.middle_name}
                    onChange={handleChange}
                    placeholder="Middle name (optional)"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    placeholder="Last name"
                    required
                    data-testid="last-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="9876543210"
                    required
                    className="bg-gray-50"
                    readOnly={!!lockedFields.phone}
                  />
                  {lockedFields.phone && (
                    <p className="text-xs text-gray-500">Auto-filled from booking</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="whatsapp_no">WhatsApp Number</Label>
                  <Input
                    id="whatsapp_no"
                    name="whatsapp_no"
                    value={formData.whatsapp_no}
                    onChange={handleChange}
                    placeholder="9876543210"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date_of_birth">Date of Birth *</Label>
                  <Input
                    id="date_of_birth"
                    name="date_of_birth"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={handleChange}
                    required
                    data-testid="dob-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gender">Gender *</Label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="blood_group">Blood Group</Label>
                  <select
                    id="blood_group"
                    name="blood_group"
                    value={formData.blood_group}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="religion">Religion</Label>
                  <select
                    id="religion"
                    name="religion"
                    value={formData.religion}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select Religion</option>
                    <option value="Hindu">Hindu</option>
                    <option value="Muslim">Muslim</option>
                    <option value="Christian">Christian</option>
                    <option value="Sikh">Sikh</option>
                    <option value="Buddhist">Buddhist</option>
                    <option value="Jain">Jain</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="caste_category">Caste Category</Label>
                  <select
                    id="caste_category"
                    name="caste_category"
                    value={formData.caste_category}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select Category</option>
                    <option value="General">General</option>
                    <option value="OBC">OBC (Other Backward Class)</option>
                    <option value="SC">SC (Scheduled Caste)</option>
                    <option value="ST">ST (Scheduled Tribe)</option>
                    <option value="EWS">EWS (Economically Weaker Section)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="aadhar_no">Aadhar Number</Label>
                  <Input
                    id="aadhar_no"
                    name="aadhar_no"
                    value={formData.aadhar_no}
                    onChange={handleChange}
                    placeholder="1234 5678 9012"
                    maxLength={12}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="medium_of_instruction">Medium of Instruction</Label>
                  <select
                    id="medium_of_instruction"
                    name="medium_of_instruction"
                    value={formData.medium_of_instruction}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select Medium</option>
                    {languages.map(lang => (
                      <option key={lang.id} value={lang.name}>{lang.name} Medium</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Full address"
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <select
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select State</option>
                    <option value="West Bengal">West Bengal</option>
                    <option value="Andhra Pradesh">Andhra Pradesh</option>
                    <option value="Assam">Assam</option>
                    <option value="Bihar">Bihar</option>
                    <option value="Delhi">Delhi</option>
                    <option value="Goa">Goa</option>
                    <option value="Gujarat">Gujarat</option>
                    <option value="Jharkhand">Jharkhand</option>
                    <option value="Karnataka">Karnataka</option>
                    <option value="Kerala">Kerala</option>
                    <option value="Madhya Pradesh">Madhya Pradesh</option>
                    <option value="Maharashtra">Maharashtra</option>
                    <option value="Odisha">Odisha</option>
                    <option value="Punjab">Punjab</option>
                    <option value="Rajasthan">Rajasthan</option>
                    <option value="Tamil Nadu">Tamil Nadu</option>
                    <option value="Telangana">Telangana</option>
                    <option value="Uttar Pradesh">Uttar Pradesh</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="district">District</Label>
                  <select
                    id="district"
                    name="district"
                    value={formData.district}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                    disabled={!formData.state}
                  >
                    <option value="">Select District</option>
                    {getDistricts().map(district => (
                      <option key={district} value={district}>{district}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pin_code">PIN Code</Label>
                  <Input
                    id="pin_code"
                    name="pin_code"
                    value={formData.pin_code}
                    onChange={handleChange}
                    placeholder="700001"
                    maxLength={6}
                  />
                </div>
              </div>
            )}

            {/* Step 2: Academic Details */}
            {currentStep === 2 && (
              <div className="space-y-6">
                {/* 10th Details */}
                <div>
                  <h3 className="text-lg font-medium mb-4">Class 10th Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="class_10_board">Board</Label>
                      <Input
                        id="class_10_board"
                        name="class_10_board"
                        value={formData.class_10_board}
                        onChange={handleChange}
                        placeholder="CBSE / State Board"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="class_10_school">School Name</Label>
                      <Input
                        id="class_10_school"
                        name="class_10_school"
                        value={formData.class_10_school}
                        onChange={handleChange}
                        placeholder="School name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="class_10_year">Year of Passing</Label>
                      <Input
                        id="class_10_year"
                        name="class_10_year"
                        value={formData.class_10_year}
                        onChange={handleChange}
                        placeholder="2020"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="class_10_percentage">Percentage / CGPA</Label>
                      <Input
                        id="class_10_percentage"
                        name="class_10_percentage"
                        value={formData.class_10_percentage}
                        onChange={handleChange}
                        placeholder="85%"
                      />
                    </div>
                  </div>
                </div>

                {/* 12th Details */}
                <div>
                  <h3 className="text-lg font-medium mb-4">Class 12th Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="class_12_board">Board</Label>
                      <Input
                        id="class_12_board"
                        name="class_12_board"
                        value={formData.class_12_board}
                        onChange={handleChange}
                        placeholder="CBSE / State Board"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="class_12_school">School/College Name</Label>
                      <Input
                        id="class_12_school"
                        name="class_12_school"
                        value={formData.class_12_school}
                        onChange={handleChange}
                        placeholder="College name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="class_12_year">Year of Passing</Label>
                      <Input
                        id="class_12_year"
                        name="class_12_year"
                        value={formData.class_12_year}
                        onChange={handleChange}
                        placeholder="2022"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="class_12_percentage">Percentage / CGPA</Label>
                      <Input
                        id="class_12_percentage"
                        name="class_12_percentage"
                        value={formData.class_12_percentage}
                        onChange={handleChange}
                        placeholder="80%"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Family Details */}
            {currentStep === 3 && (
              <div className="space-y-6">
                {/* Father's Details */}
                <div>
                  <h3 className="text-lg font-medium mb-4">Father's Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="father_name">Father's Name</Label>
                      <Input
                        id="father_name"
                        name="father_name"
                        value={formData.father_name}
                        onChange={handleChange}
                        placeholder="Father's name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="father_phone">Father's Phone</Label>
                      <Input
                        id="father_phone"
                        name="father_phone"
                        value={formData.father_phone}
                        onChange={handleChange}
                        placeholder="9876543210"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="father_occupation">Father's Occupation</Label>
                      <select
                        id="father_occupation"
                        name="father_occupation"
                        value={formData.father_occupation}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border rounded-md"
                      >
                        <option value="">Select Occupation</option>
                        {occupationOptions.map(occ => (
                          <option key={occ} value={occ}>{occ}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Mother's Details */}
                <div>
                  <h3 className="text-lg font-medium mb-4">Mother's Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="mother_name">Mother's Name</Label>
                      <Input
                        id="mother_name"
                        name="mother_name"
                        value={formData.mother_name}
                        onChange={handleChange}
                        placeholder="Mother's name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mother_phone">Mother's Phone</Label>
                      <Input
                        id="mother_phone"
                        name="mother_phone"
                        value={formData.mother_phone}
                        onChange={handleChange}
                        placeholder="9876543210"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mother_occupation">Mother's Occupation</Label>
                      <select
                        id="mother_occupation"
                        name="mother_occupation"
                        value={formData.mother_occupation}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border rounded-md"
                      >
                        <option value="">Select Occupation</option>
                        {occupationOptions.map(occ => (
                          <option key={occ} value={occ}>{occ}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Documents Upload */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800">
                    📄 <strong>Upload Documents:</strong> Please upload clear copies of required documents. 
                    {documentSettings.length > 0 
                      ? ' Document requirements are based on your course settings.'
                      : ' Accepted formats: JPG, PNG, PDF (Max 5MB each)'
                    }
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {getEffectiveDocumentTypes().map(docType => (
                    <div 
                      key={docType.id}
                      className={`border-2 rounded-lg p-4 ${
                        isDocumentUploaded(docType.id) 
                          ? 'border-green-300 bg-green-50' 
                          : docType.required 
                            ? 'border-orange-300 bg-orange-50' 
                            : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {docType.name}
                            {docType.required && <span className="text-red-500 ml-1">*</span>}
                          </h4>
                          {docType.description && (
                            <p className="text-xs text-gray-600 mt-0.5">{docType.description}</p>
                          )}
                          <p className="text-xs text-gray-500">
                            {isDocumentUploaded(docType.id) ? '✓ Uploaded' : 'Not uploaded yet'}
                            {docType.max_size_kb && ` • Max: ${docType.max_size_kb}KB`}
                            {docType.allowed_formats && ` • ${docType.allowed_formats.join(', ')}`}
                          </p>
                        </div>
                        {isDocumentUploaded(docType.id) && (
                          <CheckCircle2 className="w-6 h-6 text-green-600" />
                        )}
                      </div>

                      {/* Preview for photo */}
                      {(docType.id === 'photo' || docType.id === 'passport_photo') && isDocumentUploaded(docType.id) && (
                        <div className="mb-3 flex justify-center">
                          <img 
                            src={getDocumentPreview(docType.id)} 
                            alt="Photo preview"
                            className="w-24 h-24 object-cover rounded-lg border-2 border-gray-300"
                          />
                        </div>
                      )}

                      {/* Preview for other documents */}
                      {docType.id !== 'photo' && docType.id !== 'passport_photo' && isDocumentUploaded(docType.id) && (
                        <div className="mb-3 p-2 bg-white rounded border flex items-center gap-2">
                          <Upload className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-700 truncate">
                            {uploadedDocuments.find(d => d.type === docType.id)?.file_name}
                          </span>
                        </div>
                      )}

                      <div className="flex gap-2">
                        <label className="flex-1">
                          <input
                            type="file"
                            accept={docType.allowed_formats 
                              ? docType.allowed_formats.map(f => f === 'pdf' ? 'application/pdf' : `image/${f}`).join(',')
                              : "image/jpeg,image/png,image/jpg,application/pdf"
                            }
                            onChange={(e) => handleDocumentUpload(e, docType.id)}
                            className="hidden"
                            disabled={uploadingDoc === docType.id}
                          />
                          <div className={`
                            w-full py-2 px-3 rounded-md text-sm font-medium text-center cursor-pointer transition-colors
                            ${uploadingDoc === docType.id 
                              ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                              : isDocumentUploaded(docType.id)
                                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                : 'bg-indigo-600 text-white hover:bg-indigo-700'
                            }
                          `}>
                            {uploadingDoc === docType.id ? (
                              <span className="flex items-center justify-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Uploading...
                              </span>
                            ) : isDocumentUploaded(docType.id) ? (
                              'Replace'
                            ) : (
                              'Upload'
                            )}
                          </div>
                        </label>
                        {isDocumentUploaded(docType.id) && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeDocument(docType.id)}
                            className="text-red-600 border-red-300 hover:bg-red-50"
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Upload Summary */}
                <div className="bg-gray-50 rounded-lg p-4 mt-6">
                  <h4 className="font-medium text-gray-900 mb-2">Upload Summary</h4>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-green-600">
                      ✓ {uploadedDocuments.length} documents uploaded
                    </span>
                    <span className="text-orange-600">
                      {getEffectiveDocumentTypes().filter(d => d.required && !isDocumentUploaded(d.id)).length} required remaining
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(prev => prev - 1)}
                disabled={currentStep === 1}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
              
              {currentStep < 4 ? (
                <Button onClick={() => setCurrentStep(prev => prev + 1)}>
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit} 
                  disabled={isSaving}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="submit-form-btn"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Submit Form
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
